import React, { useState, useEffect, useCallback } from "react";
import { Alert } from "antd";
import {
  WifiOutlined,
  DisconnectOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { notify } from "utils/notification";

interface NetworkStatusProps {
  showAlert?: boolean;
  position?: "fixed" | "relative";
}

type ConnectionStatus = "online" | "offline" | "server-down";

// Global state for server connectivity
let globalServerStatus: ConnectionStatus = "online";
let statusCallbacks: ((status: ConnectionStatus) => void)[] = [];
let serverDownTimeout: NodeJS.Timeout | null = null;
let isInitialLoad = true;

// Function to notify all components about server status changes
export const notifyServerDown = () => {
  // Ignore server down notifications for the first 5 seconds after initial load
  if (isInitialLoad) {
    setTimeout(() => {
      isInitialLoad = false;
    }, 5000);
    return;
  }

  // Clear any existing timeout
  if (serverDownTimeout) {
    clearTimeout(serverDownTimeout);
  }

  // Add a small delay to avoid false positives from temporary network issues
  serverDownTimeout = setTimeout(() => {
    if (globalServerStatus !== "server-down") {
      globalServerStatus = "server-down";
      statusCallbacks.forEach((callback) => callback("server-down"));
    }
    serverDownTimeout = null;
  }, 2000); // 2 second delay
};

export const notifyServerOnline = () => {
  // Clear any pending server down notification
  if (serverDownTimeout) {
    clearTimeout(serverDownTimeout);
    serverDownTimeout = null;
  }

  if (globalServerStatus === "server-down") {
    globalServerStatus = "online";
    statusCallbacks.forEach((callback) => callback("online"));
  }
};

interface NetworkStatusProps {
  showAlert?: boolean;
  position?: "fixed" | "relative";
}

const NetworkStatus: any = ({ showAlert = true, position = "fixed" }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>(globalServerStatus);
  const [showAlert_, setShowAlert_] = useState(false);

  // Subscribe to server status changes
  useEffect(() => {
    const callback = (status: ConnectionStatus) => {
      setConnectionStatus(status);
      setShowAlert_(status !== "online");
    };

    statusCallbacks.push(callback);

    return () => {
      statusCallbacks = statusCallbacks.filter((cb) => cb !== callback);
    };
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (connectionStatus === "offline") {
        setConnectionStatus("online");
        setShowAlert_(false);
        globalServerStatus = "online";
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setConnectionStatus("offline");
      setShowAlert_(true);
      globalServerStatus = "offline";
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial check
    if (!navigator.onLine) {
      setConnectionStatus("offline");
      setShowAlert_(true);
      globalServerStatus = "offline";
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [connectionStatus]);

  if (!showAlert) {
    return null;
  }

  const getAlertProps = () => {
    switch (connectionStatus) {
      case "offline":
        return {
          message: "You are currently offline",
          description: "Please check your internet connection.",
        };
      case "server-down":
        return {
          message: "Server Connection Lost",
          description:
            "Connection to the server has been lost. Please wait while we try to reconnect, or refresh the page if the issue persists.",
        };
      default:
        return null;
    }
  };

  const alertProps = getAlertProps();

  if (!alertProps || !showAlert_) {
    return null;
  }

  return notify.error({
    message: alertProps.message,
    description: alertProps.description,
  });
};

export default NetworkStatus;
